import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  Flex,
  Text,
  Heading,
  Container,
  Button,
} from "@radix-ui/themes";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputField from "@/components/form/InputField";
import { authService } from "@/services/breeze/AuthService";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

export default function ResendConfirmationPage() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const initialEmail = searchParams.get("email") || "";

  const handleSubmit = async (values: { email: string }) => {
    setError("");
    setIsLoading(true);

    try {
      await authService.resendConfirmationEmail(values.email);
      setSuccess(true);
    } catch (error) {
      console.error("Error resending confirmation email:", error);
      setError(
        "Unable to resend confirmation email. Please try again later or contact support.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    const redirectUri = searchParams.get("redirect_uri");
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" gap="4" align="center">
              <Heading size="5" weight="medium">
                Confirmation Email Sent
              </Heading>
              <Text size="2" color="gray" align="center">
                If your email address is correct, we&apos;ll send a new
                confirmation email to your inbox. Please check your email and
                click the confirmation link.
              </Text>
              {redirectUri ? (
                <>
                  <Text size="2" color="gray" align="center">
                    Click below to continue to your application.
                  </Text>
                  <Button
                    onClick={() => {
                      if (redirectUri && redirectUri.startsWith("/") && !redirectUri.startsWith("//")) {
                        window.location.href = redirectUri;
                      }
                    }}
                    style={{ width: "100%" }}
                  >
                    Continue to Application
                  </Button>
                </>
              ) : (
                <Text size="2" color="gray" align="center">
                  Please return to your application once confirmed.
                </Text>
              )}
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      size="1"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <Card style={{ width: "100%", maxWidth: "28rem" }}>
        <Box p="6">
          <Flex direction="column" gap="6">
            <Flex direction="column" align="center" gap="2">
              <Heading size="5" weight="medium">
                Resend Confirmation Email
              </Heading>
              <Text color="gray" align="center" size="2">
                Enter your email address to receive a new confirmation link
              </Text>
            </Flex>

            {error && (
              <Box
                p="3"
                style={{
                  backgroundColor: "var(--red-a3)",
                  border: "1px solid var(--red-a6)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text color="red" size="2">
                  {error}
                </Text>
              </Box>
            )}

            <Formik
              initialValues={{
                email: initialEmail,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {() => (
                <Form>
                  <Flex direction="column" gap="4">
                    <InputField
                      name="email"
                      label="Email Address"
                      type="email"
                      required
                      options={{
                        placeholder: "Enter your email address",
                      }}
                    />

                    <Button
                      type="submit"
                      loading={isLoading}
                      style={{ width: "100%" }}
                      disabled={isLoading}
                    >
                      {isLoading ? "Sending..." : "Resend Confirmation"}
                    </Button>

                    {searchParams.get("redirect_uri") && (
                      <Button
                        variant="soft"
                        onClick={() => {
                          const redirectUri = searchParams.get("redirect_uri");
                          if (redirectUri && redirectUri.startsWith("/") && !redirectUri.startsWith("//")) window.location.href = redirectUri;
                        }}
                        style={{ width: "100%" }}
                      >
                        Continue to Application
                      </Button>
                    )}
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </Box>
      </Card>
    </Container>
  );
}
